package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.theme.StateLayerCompositor
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.math.min

/**
 * 复选框渲染器（接入主题）。
 * - 背景：DSL > 主题 `surface`；SV 圆角 / 边框 / 渐变存在时走 styled batch（[ControlSurfaceRenderer]）
 * - 边框：覆盖 focusBorderColor > focused 主题 primary > 主题 outline（仅退化路径画 4 条边）
 * - 选中态：内部 checkInset（默认 2px）边距填充 checkColor（默认 primary）
 * - 状态层：逐态覆盖优先，否则主题默认；hover/pressed 在控件整体叠加
 */
@Suppress("MagicNumber")
class CheckboxRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val cb = element as Checkbox
        val t = ThemeAccess.current()

        val focusBorder = cb.focusBorderColor() ?: t.primary
        // 未聚焦边框：逐实例 boxBorderColor 覆盖优先，否则回退主题 outline。
        val unfocusedBorder = cb.boxBorderColor() ?: t.outline
        val border = if (cb.focused()) focusBorder else unfocusedBorder
        // 外框背景：逐实例 boxBackgroundColor 覆盖优先，否则回退主题 surface。
        val boxBg = cb.boxBackgroundColor() ?: t.surface

        // 背景 + 边框：SV 圆角 / 渐变存在时走 styled 路径，否则退化为 fillRect 背景 + 4 条边框。
        ControlSurfaceRenderer.drawSurface(bridge, cb, box, boxBg, border, t.borderWidth)

        if (cb.checked()) {
            val baseInset = cb.checkInset() ?: 2f
            val pad = min(baseInset, min(box.width(), box.height()) / 4f)
            val check = cb.checkColor() ?: t.primary
            bridge.fillRect(
                box.x() + pad,
                box.y() + pad,
                box.width() - 2f * pad,
                box.height() - 2f * pad,
                check,
            )
        }

        val overrides = StateLayerCompositor.overrides(
            cb.hoverOverlayColor(),
            cb.hoverOverlayOpacity(),
            cb.focusOverlayColor(),
            cb.focusOverlayOpacity(),
            cb.pressedOverlayColor(),
            cb.pressedOverlayOpacity(),
        )
        StateLayerCompositor.overlay(bridge, cb, box, t, false, overrides, false)
    }
}
